/**
 * A stable identity for "the same lift", derived from the exercise NAME.
 *
 * Weight history used to match on exercise_id, but ids are not stable across
 * programs: a program built with the picker stores library ids
 * (`ub-pull-chest-supported-row`) while an imported one stores slugs of its own
 * wording (`chest-supported-row`). Changing an athlete's program therefore hid
 * all their previous lifts. The id resolver also collapsed different lifts onto
 * one id (dumbbell bench → barbell bench), mixing their PBs.
 *
 * Rules, each driven by names that actually appear in the logs:
 *  - wording, punctuation, case, plurals and word order don't matter
 *      "Chest-Supported Row" = "Chest-supported row"; "Goblet box squat" = "Goblet squat to box"
 *  - coaching cues are ignored — after a spaced dash, a comma, in [brackets], or
 *    in (parentheses) that aren't just an implement
 *      "Seated calf raise — 2 up, 1 down" = "Seated Calf (2 up 1 down)" = "Seated Calf Raise"
 *  - the implement is kept, so different lifts stay apart
 *      "Dumbbell bench press" ≠ "Barbell Bench Press"; "Kettlebell RDL" ≠ "RDL"
 *  - implied implements are filled in for lifts that only come one way
 *      "Lat Pulldown" = "Cable Lat Pulldown"; "RDL" = "Barbell Romanian Deadlift"
 *  - known synonyms fold together
 *      "Bulgarian Split Squat" = "Rear-Foot Elevated Split Squat"; "1-arm DB row" = "Single-Arm Dumbbell Row"
 *
 * Deliberately NOT merged:
 *  - load-changing modifiers (paused, tempo, eccentric, box, heel-elevated) —
 *    a paused squat PB shouldn't be judged against a normal one
 *  - either/or prescriptions ("Chin-up or lat pulldown") — no way to know which
 *    was actually lifted, so they keep their own history
 */

const IMPLEMENT_WORDS: [RegExp, string][] = [
  [/\b(dumbbells?|dbs?)\b/g, 'db'],
  [/\b(barbells?|bb)\b/g, 'bb'],
  [/\b(kettlebells?|kbs?)\b/g, 'kb'],
  [/\b(trap bar|hex bar|trapbar|hexbar)\b/g, 'trapbar'],
  [/\b(ez bar|ezbar)\b/g, 'ezbar'],
  [/\b(smith machine|smith)\b/g, 'smith'],
  [/\bcables?\b/g, 'cable'],
  [/\blandmines?\b/g, 'landmine'],
]
const IMPLEMENTS = new Set(['db', 'bb', 'kb', 'trapbar', 'ezbar', 'smith', 'cable', 'landmine'])

/** Whole-phrase synonyms, applied before tokenising. */
const PHRASES: [RegExp, string][] = [
  [/\bromanian deadlifts?\b/g, 'rdl'],
  [/\bconventional deadlifts?\b/g, 'deadlift'],
  [/\bbulgarian split squats?\b/g, 'rear foot elevated split squat'],
  [/\b(rfess|rfe split squats?)\b/g, 'rear foot elevated split squat'],
  [/\b(single arm|one arm|1 arm)\b/g, 'sa'],
  [/\b(single leg|one leg|1 leg)\b/g, 'sl'],
  [/\bfarmer s\b/g, 'farmer'],
  [/\bfarmer walk\b/g, 'farmer carry'],
]

/** Single-word spellings of the same thing. */
const WORDS: Record<string, string> = {
  triceps: 'tricep', pressdown: 'pushdown', pause: 'paused', heels: 'heel',
}

/** Words that add nothing to identity. */
const FILLER = new Set(['to', 'the', 'a', 'an', 'with', 'and', 'from', 'flat', 'of'])

const sortedKey = (words: Iterable<string>) => [...new Set(words)].sort().join(' ')

/** Lifts that only really come one way when the implement isn't stated. */
const IMPLIED: [string, string][] = [
  ...['rdl', 'bench press', 'back squat', 'front squat', 'box squat', 'deadlift',
      'overhead press', 'hip thrust', 'good morning', 'zercher squat', 'push press']
    .map((l): [string, string] => [l, 'bb']),
  ...['lat pulldown', 'tricep pushdown', 'face pull', 'pallof press', 'seated row']
    .map((l): [string, string] => [l, 'cable']),
].map(([lift, imp]) => [sortedKey(lift.split(' ')), imp])
const IMPLIED_BY_KEY = new Map(IMPLIED)

/** Modifiers that change the load, so they're kept even from inside brackets. */
const LOAD_MODIFIERS = new Set(['eccentric', 'paused', 'pause', 'tempo', 'isometric'])

const singular = (w: string) =>
  w.length > 3 && w.endsWith('s') && !/(ss|us|is)$/.test(w) ? w.slice(0, -1) : w

function normalise(text: string): string {
  let s = text.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
  for (const [re, to] of IMPLEMENT_WORDS) s = s.replace(re, to)
  for (const [re, to] of PHRASES) s = s.replace(re, to)
  return s
}

export function exerciseKey(name: string | null | undefined): string {
  if (!name) return ''
  let s = name.toLowerCase().replace(/\//g, ' or ')

  // Cues: [brackets], then anything after a spaced dash or a comma.
  s = s.replace(/\[[^\]]*\]/g, ' ')
  s = s.split(/\s+[—–-]\s+|,/)[0]

  // (parentheses) survive only when they name the implement — "(Barbell)",
  // "(DB loaded)", "(Cable Rope)". Anything else in brackets is a cue.
  let bracketImplements: string[] = []
  s = s.replace(/\(([^)]*)\)/g, (_m, inner: string) => {
    const words = normalise(inner).split(' ').filter(Boolean)
    const imps = words.filter((w) => IMPLEMENTS.has(w))
    if (imps.length && words.length <= 2) bracketImplements = bracketImplements.concat(imps)
    bracketImplements = bracketImplements.concat(words.filter((w) => LOAD_MODIFIERS.has(w)))
    return ' '
  })

  const words = normalise(s)
    .split(' ')
    .filter(Boolean)
    .concat(bracketImplements)
    .map(singular)
    .map((w) => WORDS[w] ?? w)
    .filter((w) => !FILLER.has(w))

  // A calf "raise" is implied: "Seated Calf (2 up 1 down)" is a seated calf raise.
  if (words.includes('calf') && !words.includes('raise')) words.push('raise')
  // "Alternating Dumbbell Bench" is a bench press.
  if (words.includes('bench') && !words.includes('press')) words.push('press')
  // "Incline Dumbbell Press" is an incline bench press.
  if (words.includes('incline') && words.includes('press') && !words.includes('bench')) words.push('bench')

  const hasImplement = words.some((w) => IMPLEMENTS.has(w))
  if (!hasImplement) {
    const implied = IMPLIED_BY_KEY.get(sortedKey(words))
    if (implied) words.push(implied)
  }
  return sortedKey(words)
}
